import { Formik } from 'formik';
import React, { useState } from 'react';
import { Form } from 'react-bootstrap';
import Turnstile, { useTurnstile } from 'react-turnstile';

import { PrimaryButton, ProButton } from '@components/Button/variants';
import DatePickerField from '@components/Form/DatePickerField';
import TextField from '@components/Form/TextField';
import SectionBreakTitle, { SectionColor } from '@components/SectionBreakTitle';
import useUserCreateForSubscribePage from '@hooks/useUserCreateForSubscribePage';
import useValidateGiftRecipient from '@hooks/useValidateGiftRecipient';
import { CLOUDFLARE_SITE_KEY } from '@src/env';
import { SelectedOffer } from '@utils/backend-api/subscribe';
import { TURNSTILE_ACTIONS } from '@utils/constants';
import { FormLocation, LeadTypes } from '@utils/enums';

import { giftRecipientSchema } from './GiftRecipientSchema';

interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  subscriptionStartDate: string;
}

const initialState = {
  firstName: '',
  lastName: '',
  email: '',
  subscriptionStartDate: '',
};

interface GiftRecipientFormProps {
  giftRecipientCustomerId?: string;
  giverStripeCustomerId?: string;
  selectedOffer: SelectedOffer;
  setGiftrecipientCustomerId: (customerId: string) => void;
  setSubscriptionStartDate: (startDate: string) => void;
}

const GiftRecipientForm = ({
  selectedOffer,
  setGiftrecipientCustomerId,
  setSubscriptionStartDate,
  giftRecipientCustomerId,
  giverStripeCustomerId,
}: GiftRecipientFormProps) => {
  const Button = selectedOffer.isPro ? ProButton : PrimaryButton;
  const { mutateAsync: createUser, isLoading } = useUserCreateForSubscribePage();
  const { mutateAsync: validateGiftRecipient, isLoading: isValidateGiftRecipientLoading } =
    useValidateGiftRecipient();
  const turnstile = useTurnstile();
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  const handleCreateRecipient = async (values: FormValues) => {
    const sanitizedParams = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
    };
    const response = await validateGiftRecipient(sanitizedParams);
    if (!response.success) {
      setErrorMessage(
        'Sorry, we are not able to process the gift subscription with current recipient'
      );
      turnstile.reset();
      setTurnstileToken(null);
      return;
    }
    setErrorMessage('');
    if (!response.email || !response.stripeCustomerId) {
      try {
        const createUserParams = {
          name: `${values.firstName} ${values.lastName}`,
          declined_opt_in: false,
          email: values.email,
          form_location: FormLocation.Gift,
          lead_type: LeadTypes.Gift,
          opt_in_required: false,
          page: window.location.pathname,
          skip_verify_new_email: true,
          signup_for_gift_recipient: true,
          turnstile_token: turnstileToken,
        };
        const { stripeCustomerId } = await createUser(createUserParams);
        setGiftrecipientCustomerId(stripeCustomerId);
      } catch (error) {
        turnstile.reset();
        setTurnstileToken(null);
        return;
      }
    } else {
      setGiftrecipientCustomerId(response.stripeCustomerId);
    }
    setSubscriptionStartDate(values.subscriptionStartDate);
  };

  return (
    <div className="gift-recipient-account-info m-auto mt-32 max-w-md sm:max-w-full">
      <Formik
        enableReinitialize
        initialValues={initialState}
        onSubmit={handleCreateRecipient}
        validationSchema={giftRecipientSchema}
      >
        {({ handleSubmit }) => (
          <Form onSubmit={handleSubmit}>
            <SectionBreakTitle
              title="2.Gift recipient"
              color={selectedOffer.isPro ? SectionColor.Gold : SectionColor.Magenta}
              className="mb-24"
            />
            {giverStripeCustomerId && (
              <>
                <div className="mb-24 flex gap-4">
                  <TextField
                    label="First Name"
                    name="firstName"
                    className="flex-1"
                    disabled={!!giftRecipientCustomerId}
                  />
                  <TextField
                    label="Last Name"
                    name="lastName"
                    className="flex-1"
                    disabled={!!giftRecipientCustomerId}
                  />
                </div>
                <TextField
                  label="Email"
                  name="email"
                  disabled={!!giftRecipientCustomerId}
                  className="mb-24"
                />
                <DatePickerField
                  name="subscriptionStartDate"
                  disabled={!!giftRecipientCustomerId}
                  label="Subscription Start Date"
                  darkMode={true}
                  maxDate={new Date(Date.now() + 6 * 30 * 24 * 60 * 60 * 1000)}
                  minDate={new Date(Date.now())}
                />
                <Turnstile
                  action={TURNSTILE_ACTIONS.giftRecipient}
                  className="mt-8"
                  fixedSize
                  theme="light"
                  sitekey={CLOUDFLARE_SITE_KEY}
                  refreshExpired="auto"
                  onSuccess={(token) => setTurnstileToken(token)}
                  onExpire={() => setTurnstileToken(null)}
                  onError={() => setTurnstileToken(null)}
                />
                {errorMessage && (
                  <span className="text-disclosure-sm text-primary-magenta">{errorMessage}</span>
                )}
                <Button
                  as="button"
                  type="submit"
                  className="mt-24 w-full"
                  isLoading={isLoading}
                  disabled={
                    isLoading ||
                    !!giftRecipientCustomerId ||
                    !turnstileToken ||
                    isValidateGiftRecipientLoading
                  }
                >
                  Continue
                </Button>
              </>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default GiftRecipientForm;
